import { defineComponent as _defineComponent } from 'vue'
import { useGetJobGeoJson } from '@/api/field-view'
import GeoJsonSource from '@/components/field-view-next/mapbox/GeoJsonSource.vue'
import LayerGroup from '@/components/field-view-next/mapbox/LayerGroup.vue'
import { computed, toRef, watchEffect } from 'vue'
import mapboxgl from 'mapbox-gl'
import length from '@turf/length'
import { useMap } from '../mapbox/context'
import moment from 'moment'
import { GeoJsonProperties } from 'geojson'
import { useFieldView } from '../FieldViewState'
import { locationPinStyle } from './styles/location-pin'
import { resourcePinStyle } from './styles/resource-pin'


export default /*#__PURE__*/_defineComponent({
  __name: 'SingleJobLayer',
  props: {
    selectedJobNumber: null
  },
  emits: ["total-distance-calculated"],
  setup(__props: any, { emit: emits }: { emit: ({
  (event: 'total-distance-calculated', data: number)
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  selectedJobNumber: string | number
}





const { map, mini } = useMap()
const { setSelectedLocation, setSelectedResource } = useFieldView()

const jobNumber = toRef(props, 'selectedJobNumber')
const { data } = useGetJobGeoJson(jobNumber)

const sourceName = computed(() => `job-${jobNumber.value}`)

const getHoverContent = ({ properties }: mapboxgl.MapboxGeoJSONFeature) => {
  if (!properties) return ''

  const unit = properties.UnitNumber
  const user = properties.UserName

  if (!unit || !user) {
    return ''
  }

  const time = moment(properties.Timestamp).format('MMM DD, HH:mm')
  const html = `${unit} (${user}) on ${time}`

  return html
}

const layers = computed<mapboxgl.AnyLayer[]>(() => [
  {
    id: `job-routes-border`,
    type: 'line',
    source: sourceName.value,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': 'DarkBlue',
      'line-width': 10,
    },
    filter: ['==', '$type', 'LineString'],
  },
  {
    id: `job-routes`,
    type: 'line',
    source: sourceName.value,
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': 'CornflowerBlue',
      'line-width': 8,
    },
    filter: ['==', '$type', 'LineString'],
  },
  {
    id: `job-breadcrumb-circle`,
    source: sourceName.value,
    type: 'circle',
    paint: {
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        12,
        0,
        14,
        3,
        22,
        10,
      ],
      'circle-color': 'blue',
      'circle-stroke-color': 'white',
      'circle-stroke-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        12,
        0,
        22,
        2,
      ],
    },
    filter: ['==', ['get', 'Type'], 'Breadcrumb'],
  },
  {
    id: `job-breadcrumb-bearing`,
    source: sourceName.value,
    type: 'symbol',
    layout: {
      'text-field': '➔',
      'text-size': ['interpolate', ['linear'], ['zoom'], 14, 0, 16, 10, 22, 20],
      'text-rotate': ['-', ['get', 'Heading'], 90],
    },
    paint: {
      'text-color': 'white',
      'text-translate': [-0.1, 0],
    },
    filter: ['==', ['get', 'Type'], 'Breadcrumb'],
  },
  {
    id: `job-start-finish`,
    source: sourceName.value,
    type: 'symbol',
    layout: {
      'icon-size': 1,
      'icon-rotate': ['get', 'Heading'],
      'icon-allow-overlap': true,
      'icon-rotation-alignment': 'map',
      'icon-image': 'arrow-standby',
      'text-field': ['get', 'Description'],
      'text-font': ['Montserrat Medium', 'Arial Unicode MS Regular'],
      'text-radial-offset': 1,
      'text-line-height': 1.1,
      'text-optional': true,
      'text-variable-anchor': ['right', 'left', 'top', 'bottom'],
      'text-size': [
        'interpolate',
        ['cubic-bezier', 0.5, 0, 1, 1],
        ['zoom'],
        11,
        11,
        15,
        16,
      ],
    },
    paint: {
      'text-halo-color': 'hsl(0, 5%, 0%)',
      'text-color': 'white',
      'text-halo-width': [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        3,
        2,
      ],
      'text-halo-blur': 0.5,
    },
    filter: [
      'any',
      ['==', ['get', 'SubType'], 'StartPoint'],
      ['==', ['get', 'SubType'], 'EndPoint'],
    ],
  },
  {
    id: `job-locations`,
    source: sourceName.value,
    type: 'symbol',
    layout: {
      ...locationPinStyle.layout,
      'symbol-sort-key': 0,
      'icon-allow-overlap': true,
      'icon-size': 1.5,
      'text-radial-offset': 2,
      'text-allow-overlap': true,
      'text-optional': false,
      'text-size': {
        stops: [
          [15, 11],
          [15.5, 12],
          [17, 14],
        ],
      },
    },
    paint: {
      ...locationPinStyle.paint,
      'text-opacity': 1,
    },
    filter: [
      'any',
      ['==', ['get', 'Type'], 'DestinationLocation'],
      ['==', ['get', 'Type'], 'PickupLocation'],
    ],
  },
  {
    id: `job-resources`,
    type: 'symbol',
    source: sourceName.value,
    layout: {
      ...resourcePinStyle.layout,
      'symbol-sort-key': 0,
      'icon-size': 1.5,
      'icon-allow-overlap': false,
      'icon-optional': false,
      'text-radial-offset': 2,
      'text-allow-overlap': true,
      'text-optional': false,
      'text-size': {
        stops: [
          [15, 11],
          [15.5, 12],
          [17, 14],
        ],
      },
    },
    paint: {
      ...resourcePinStyle.paint,
      'text-opacity': 1,
    },
    filter: ['==', ['get', 'Type'], 'Resource'],
  },
])

// we need to format the times of the start/end of the job
// otherwise they display in UTC. We found no good way to format
// them using the mapbox style expressions.
const formattedData = computed(() =>
  data.value
    ? {
        ...data.value,
        features: data.value?.features.map((f) => ({
          ...f,
          properties: {
            ...f.properties,
            Description: buildDescriptionForStartAndEndPoints(f.properties),
          },
        })),
      }
    : null
)

const buildDescriptionForStartAndEndPoints = (
  properties?: GeoJsonProperties
) => {
  if (!properties) return ''

  if (properties.SubType === 'StartPoint') {
    return `${moment(properties.Timestamp).format('MMM DD, HH:mm')}`
  } else if (properties.SubType === 'EndPoint') {
    return `${moment(properties.Timestamp).format('MMM DD, HH:mm')}`
  }

  return ''
}

watchEffect(() => {
  const features = data.value?.features
  if (!features) return

  const bounds = new mapboxgl.LngLatBounds()

  features.forEach((f) => {
    if (f.geometry.type === 'Point') {
      bounds.extend(f.geometry.coordinates as [number, number])
    } else if (f.geometry.type === 'LineString') {
      for (const coord of f.geometry.coordinates) {
        bounds.extend(coord as [number, number])
      }
    } else if (f.geometry.type === 'MultiLineString') {
      for (const group of f.geometry.coordinates) {
        for (const coord of group) {
          bounds.extend(coord as [number, number])
        }
      }
    }
  })

  try {
    const mapFitOptions = mini.value
      ? { padding: 60 }
      : {
          padding: {
            top: 150,
            bottom: 150,
            left: 150,
            right: 150 + 500, // accounts for right side panel
          },
        }

    map.value?.fitBounds(bounds, {
      ...mapFitOptions,
      maxZoom: 16,
    })

    // to avoid doubling up on symbols shown in the map
    // once the single job layer has loaded, we can hide
    // any selected items.  Otherwise sometimes we show
    // the location / resource pin twice, but with just
    // slight offsets and then it looks funny.
    setSelectedLocation(null)
    setSelectedResource(null)
  } catch (e) {
    console.error(e)
  }
})

watchEffect(() => {
  if (data.value && data.value.features.length > 0) {
    const lineStrings = data.value.features.filter(
      (f) => f.properties?.Type === 'Route'
    )

    const totalDistance = lineStrings.reduce((a, b) => a + length(b), 0)
    emits('total-distance-calculated', totalDistance)
  }
})

return { __sfc: true,props, emits, map, mini, setSelectedLocation, setSelectedResource, jobNumber, data, sourceName, getHoverContent, layers, formattedData, buildDescriptionForStartAndEndPoints, GeoJsonSource, LayerGroup }
}

})